/* =====================================================================
   mw-shopify-cart.js — the cart half of the board designer.
   ---------------------------------------------------------------------
   The lifted configurator ends at one call:

       MW.addToCart({ sku, name, unit, props })

   On the static site that wrote a line into localStorage. Here it adds
   real line items to the Shopify cart. Nothing else in the configurator
   knows the difference, which is why the port is a file rather than a
   rewrite.

   HOW A CONFIGURED BOARD BECOMES LINE ITEMS

   Shopify prices a variant, and a variant is size × shape × material.
   Everything else the customer chose — the engraving, the filled
   colour, the rubber feet, the message on the back — is an add-on, and
   each one is added as its OWN line item from the hidden
   "Options & add-ons" product, tagged with a property naming the piece
   it belongs to.

   That is deliberate. The alternative, folding the extras into the
   variant price, would need a variant for every combination (45 × 8)
   and would still not let Todd see on the packing slip what he owes
   the customer. This way the order reads:

       Personalized Cutting Board — Medium / Arched / Walnut   $155
         Line 1: The Mellons
         Line 2: est. 3-19-18
       Options & add-ons — Engraving — front (+$30)             $30
         For: Personalized Cutting Board

   THE INVARIANT

   The sum of the line items must equal the price the page showed. The
   configurator hands its own total over in `unit`, so this file checks
   its arithmetic against it and refuses to add anything if the two
   disagree. A silent mismatch is the one failure mode that costs money
   in both directions.
===================================================================== */
(function () {
  'use strict';

  window.MW = window.MW || {};

  /* ---------------------------------------------------------------
     MWCart.props — ported verbatim from the static site's cart.js.

     The lifted configurator calls this to gather what the customer
     chose. It was the one piece of the old cart the designer still
     depended on, and leaving it out is what made the Add button do
     nothing at all: the click threw before it ever reached
     MW.addToCart. Ported rather than rewritten, because the rules in
     here are specific — data-label beats value, a repeated key
     overwrites rather than duplicates, and the fill colour lives in a
     closure and has to be read back off the DOM.
  --------------------------------------------------------------- */
  var Cart = window.MWCart = window.MWCart || {};

  Cart.props = function (scope) {
    var out = [], seen = {};
    var root_ = scope || document;
    [].forEach.call(root_.querySelectorAll('[data-prop]'), function (el) {
      var k = el.getAttribute('data-prop'), v = null;
      if (el.type === 'radio' || el.type === 'checkbox') {
        if (!el.checked) return;
        v = el.type === 'checkbox'
          ? (el.getAttribute('data-label') || 'Yes')
          : (el.getAttribute('data-label') || el.value);
      } else if (el.tagName === 'SELECT') {
        var o = el.options[el.selectedIndex];
        if (o) v = o.getAttribute('data-label') || o.textContent;
      } else {
        v = el.value;
      }
      if (v === null || String(v).trim() === '') return;
      if (seen[k]) { out[seen[k] - 1][1] = String(v).trim(); return; }
      out.push([k, String(v).trim()]);
      seen[k] = out.length;
    });

    var sw = root_.querySelector('#fillSwatches .swatch.on, #fillSwatches [aria-pressed="true"]');
    var fillOn = root_.querySelector('#fillOn');
    if (sw && fillOn && fillOn.checked) {
      out.push(['Fill color', sw.getAttribute('aria-label') || 'selected']);
    }
    return out;
  };

  function data() {
    var el = document.getElementById('mw-variant-map');
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  /* The form's radios carry data-label, which is what the CSV import
     used as the Shopify option value — so the two line up by name. */
  function chosenOptions(form) {
    var out = [];
    form.querySelectorAll('input[type=radio]:checked').forEach(function (i) {
      out.push((i.dataset.label || i.value || '').trim());
    });
    return out;
  }

  function findVariant(map, form) {
    var want = chosenOptions(form);
    for (var i = 0; i < map.variants.length; i++) {
      var v = map.variants[i];
      var hit = want.every(function (value) { return v.options.indexOf(value) !== -1; });
      if (hit && v.options.length === want.length) return v;
    }
    return null;
  }

  /* Add-on variants are titled "Label (+$N)" by the import. Matched on
     the label, never on the price, so a price change does not silently
     stop matching. */
  function findAddon(map, label) {
    for (var i = 0; i < map.addons.length; i++) {
      if (map.addons[i].title.indexOf(label) === 0) return map.addons[i];
    }
    return null;
  }

  /* Which extras did the customer actually choose? Read off the form,
     not off the price, so this stays true when a fee changes. */
  function chosenAddons(form) {
    var picked = [];
    var text = [].slice.call(form.querySelectorAll('input[type=text], textarea'))
      .filter(function (i) { return (i.value || '').trim(); });

    /* Engraving is charged when there is any front text and the product
       does not already include it in the size price. */
    var front = text.filter(function (i) { return /line ?1|line ?2/i.test(i.id + ' ' + (i.dataset.prop || '')); });
    if (front.length) picked.push('Engraving — front');

    form.querySelectorAll('input[type=checkbox]:checked').forEach(function (c) {
      var label = (c.dataset.prop || '').trim();
      if (/feet/i.test(label)) picked.push('Rubber feet');
      else if (/back/i.test(label)) picked.push('Engraving — message on the back');
      else if (/fill/i.test(label) || c.id === 'fillOn') picked.push('Filled engraving');
      else if (/keyhole|hanging/i.test(label)) picked.push('Keyhole slots routed in back');
      else if (/paint/i.test(label)) picked.push('Paint-filled lettering');
      else if (label) picked.push(label);
    });
    return picked;
  }

  function post(items) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items })
    }).then(function (r) {
      if (!r.ok) return r.json().then(function (e) { throw new Error(e.description || 'Could not add to cart'); });
      return r.json();
    });
  }

  MW.toast = function (msg) {
    var t = document.getElementById('mw-toast');
    if (!t) {
      t = document.createElement('div');
      t.id = 'mw-toast';
      t.setAttribute('role', 'status');
      document.body.appendChild(t);
    }
    t.textContent = msg;
    t.classList.add('is-shown');
    clearTimeout(MW._toastTimer);
    MW._toastTimer = setTimeout(function () { t.classList.remove('is-shown'); }, 3200);
  };

  MW.addToCart = function (line) {
    var map = data();
    var form = document.getElementById('configForm') || document.querySelector('.mw-configurator form');
    if (!map || !form) { MW.toast('Something is missing on this page — please reload.'); return; }

    /* Two callers. The personalisation panel knows exactly which
       variant and which extras were chosen and says so; the older
       lifted configurators do not, so their choices are read back off
       the form. Explicit always wins — inference is the fallback, not
       the contract. */
    var variant = null;
    if (line.variantId) {
      for (var v = 0; v < map.variants.length; v++) {
        if (map.variants[v].id === line.variantId) variant = map.variants[v];
      }
    }
    if (!variant) variant = findVariant(map, form);
    if (!variant) { MW.toast('That combination is not available. Please choose again.'); return; }

    /* line-item properties: the engraving text, the font, the fill
       colour — everything Todd needs to cut the piece. */
    var props = {};
    (line.props || []).forEach(function (pair) {
      if (pair && pair.length === 2 && String(pair[1]).trim()) props[pair[0]] = pair[1];
    });

    var items = [{ id: variant.id, quantity: 1, properties: props }];
    var total = variant.price;

    var wanted = line.addons
      ? line.addons
      : chosenAddons(form).map(function (label) { return { label: label, qty: 1 }; });

    wanted.forEach(function (want) {
      var a = findAddon(map, want.label);
      if (!a) return;                       /* not sold as an add-on here */
      var qty = want.qty || 1;
      items.push({
        id: a.id,
        quantity: qty,
        properties: { 'For': line.name || document.title, '_parent': variant.id }
      });
      total += a.price * qty;
    });

    /* The invariant. `unit` is what the page showed the customer. */
    if (line.unit != null && Math.abs(total - line.unit) > 0.5) {
      MW.toast('Hold on — the price on screen and the price in the cart disagree. Nothing was added.');
      if (window.console) {
        console.error('[mw-cart] price mismatch: page $' + line.unit + ' vs cart $' + total, items);
      }
      return;
    }

    var button = document.getElementById('addBtn');
    if (button) { button.disabled = true; button.dataset.busy = '1'; }

    /* In the theme editor the storefront runs in a preview session that
       does not keep a cart, so an add can return 200 and leave the cart
       empty. Say so rather than claiming success. */
    if (window.Shopify && window.Shopify.designMode) {
      MW.toast('This is the theme preview — Shopify does not keep a cart here. Open the store itself to test adding.');
      if (button) { button.disabled = false; delete button.dataset.busy; }
      return;
    }

    post(items).then(function () {
      /* Do not take the 200 as proof. Read the cart back and check the
         items actually landed: an add can be accepted and still leave
         the cart empty (a preview session, a third-party cart script,
         a variant not published to this channel). Telling the customer
         "Added to your cart" when it is empty is the worst outcome. */
      return fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          var landed = (cart.items || []).some(function (i) { return i.id === variant.id; });
          if (!landed) {
            MW.toast('Shopify accepted the order but the cart came back empty. Nothing has been charged — please tell me what you were configuring.');
            if (window.console) console.error('[mw-cart] add returned ok but /cart.js has no matching line', { sent: items, cart: cart });
            return;
          }
          MW.toast('Added to your cart.');
          /* Horizon's drawer listens for 'shopify:cart:lines-update', a
             standard event built by a module this classic script cannot
             construct properly. Rather than fake it and hope the drawer
             refreshes — which is how "Added to your cart" ends up next
             to an empty drawer — go to the cart page. It is always the
             truth, and it is what the customer wants to see after
             spending five minutes designing something. */
          try {
            document.dispatchEvent(new CustomEvent('shopify:cart:lines-update',
              { bubbles: true, detail: { cart: cart, action: 'add' } }));
          } catch (e) { /* the drawer is a convenience, not the contract */ }
          window.location.href = '/cart';
        });
    }).catch(function (err) {
      MW.toast(err.message || 'Could not add to cart.');
    }).finally(function () {
      if (button) { button.disabled = false; delete button.dataset.busy; }
    });
  };
})();
